import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject, Input, Optional } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
// import { AdmissionService } from '../admission/add-admission/admission.service';
import { forkJoin, of, sample } from 'rxjs';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { CodeableConceptField, CodeField, GroupField, IndividualField, IndividualReferenceField, ReferenceFieldArray, formMetaData, generalFieldsData, SingleCodeField, codeableConceptFromBackEndDataType, CodeableConceptFieldFromBackEnd } from '../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { ErrorService } from '../shared/error.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input'
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormGroup } from '@angular/forms';
import { CommonModule, JsonPipe } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTab } from '@angular/material/tabs';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { StateService } from '../shared/state.service';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { firstValueFrom } from 'rxjs';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
// import { CarePlanFormComponent } from '../admission/care-plan-form/care-plan-form.component';
// import { CarePlanDialogService } from '../shared/care-plan-dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { TaskPreviewDialogComponent } from './task-preview-dialog/task-preview-dialog.component';
import { AuthService, capacityObject } from '../shared/auth/auth.service';
import { normalizeRole, ROLE_DISPLAY_NAMES, RoleType, SystemRoles } from '../shared/system-roles';
import { UtilityService } from '../shared/utility.service';

type FormFields = IndividualField | ReferenceFieldArray | CodeableConceptField | CodeField | IndividualReferenceField | GroupField;

// Roles that must never appear as task assignees (as a role or via a practitioner).
const EXCLUDED_OWNER_ROLE_CODES = new Set<string>([SystemRoles.RemoteDoctor]);

interface OwnerOption {
  kind: 'role' | 'practitioner';
  reference: string;
  display: string;
  type: 'PractitionerRole' | 'Practitioner';
  roleCodes: string[];
  eligible?: boolean;
}

@Component({
  selector: 'app-testing-tasks',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule,

    MatMenuModule,
    MatFormFieldModule, MatButtonModule, MatInputModule,
    MatDatepickerModule, MatSelectModule, CommonModule,
    MatTimepickerModule, MatDividerModule, MatTooltipModule, RouterLink,
    JsonPipe, MatIconModule, MatAutocompleteModule,
    SuccessMessageComponent
  ],
  templateUrl: './testing-tasks.component.html',
  providers: [provideNativeDateAdapter()],
  styleUrl: './testing-tasks.component.scss'
})
export class TestingTasksComponent {
  @Input() carePlanId?: string;
  fb = inject(FormBuilder);
  errorService = inject(ErrorService);
  dialog = inject(MatDialog);
  stateService = inject(StateService);
  http = inject(HttpClient);
  auth = inject(AuthService);
  formFieldsDataService = inject(FormFieldsSelectDataService);
  utilityService = inject(UtilityService);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
  tasksForm?: FormArray;
  // admissionService = inject(AdmissionService);
  g: any;

  // Track existing service requests for current patient
  existingServiceRequests: any[] = [];
  loadingServiceRequests = false;

  // Care plan related properties
  formFieldsToUse: any;
  showCarePlanForm = false;
  admissionSubmitted = true; // Set to true for testing-tasks component

  // Track selected service requests per task
  taskServiceRequests: Map<number, { id: string; code: string; display: string; resource?: any }> = new Map();

  // Assignee option pools, rebuilt from the (org-scoped) PractitionerRole bundle.
  // Roles = distinct normalized role codes; Practitioners = person + all their role codes.
  ownerRoleOptions: OwnerOption[] = [];
  ownerPractitionerOptions: OwnerOption[] = [];

  get taskFormArray() {
    return this.overallTasksForm?.get(['tasksForm']) as FormArray;
  }

  getElementFromFormArray($index: number) {
    return this.taskFormArray.at($index) as FormGroup;
  }
  formFields?: any[]
  ngOnInit() {
    // Fetch Practitioners and PractitionerRoles
    const practitioners$ = this.formFieldsDataService.getFormFieldSelectData('encounter', 'participant');

    // For PractitionerRole, we can fetch directly or use formFieldsDataService if defined. 
    // We will do a direct fetch since it's not explicitly in form-fields-select-data.service.ts
    const practitionerRoles$ = this.http.get<any>(`${this.backendUrl}/PractitionerRole?_format=json&_count=100`);

    forkJoin({
      testData: this.http.get("/testDta.json"),
      practitioners: practitioners$,
      practitionerRoles: practitionerRoles$
    }).subscribe(({ testData, practitioners, practitionerRoles }) => {
      this.g = testData;

      const practitionerOptions = practitioners || [];

      this.buildOwnerOptions(practitionerOptions, practitionerRoles?.entry || []);

      this.formFields = [
        //groupfield for tasks

        //taskintent, task priority, task status, task code

        //taskdescription -individualfield, textarea
        <IndividualField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskName',
            fieldName: "Task Name",
            fieldType: 'IndividualField',
            inputType: 'text',
            isArray: false,
            isGroup: false,
            auth: {
              read: 'all',
              write: 'doct  or, nurse'
            },
          },
        },
        <SingleCodeField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskFocus',
            fieldName: "Task Type",
            fieldLabel: "Type of Task",
            fieldType: 'SingleCodeField',
            inputType: 'hidden',
            isHidden: true,
            isArray: false,
            isGroup: false,
            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
          },
          data: ['Vital Signs', 'Observation', 'Medication Administration',
            'Lab Test', 'Service Requests',
          ]

        },

        // Assignee — rendered AFTER taskFocus so eligibility can be filtered by the chosen focus.
        <IndividualReferenceField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'owner',
            fieldName: "Assign To (Practitioner or Role)",
            fieldLabel: "Select Role or Practitioner",
            fieldType: 'IndividualReferenceField',
            inputType: 'select',
            isArray: false,
            isGroup: false,
            validations: [
              {
                type: 'default',
                name: 'required',
                isFunction: false
              }
            ],
            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
          },
          data: []
        },

        <IndividualField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskDescription',
            fieldName: "Task Description",
            fieldLabel: "Describe the Task",
            fieldType: 'IndividualField',
            inputType: 'textarea',
            isArray: false,
            isGroup: false,
            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
          },
        },
        //task focus

        //subfocus
        <SingleCodeField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskSubFocus',
            fieldName: "Task Sub Focus",
            fieldType: 'SingleCodeField',
            inputType: 'select',
            isArray: false,
            isGroup: false,
            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
          },
          data: [
            ['Vital Signs', ['Blood Pressure', 'Heart Rate', '9279-1$#$Respiratory Rate$#$http://loinc.org', 'Temperature']],

          ]

        },
        <SingleCodeField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskIntent',
            fieldName: "taskIntent",
            fieldType: 'SingleCodeField',
            inputType: 'hidden',
            isHidden: true,
            isArray: false,
            isGroup: false,
            value: 'order',
            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
            // data: g.taskIntent
          },

        },
        <SingleCodeField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskPriority',
            fieldName: "Task Priority",
            fieldType: 'SingleCodeField',
            // inputType: 'hidden',
            isArray: false,
            isGroup: false,

            // isHidden: true,


            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
            // data: g.taskPriority
          },
          data: [
            'routine', 'urgent', 'stat', 'asap',

          ]


        },
        <SingleCodeField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskStatus',
            fieldName: "Task Status",
            fieldType: 'SingleCodeField',
            inputType: 'hidden',
            isHidden: true,
            isArray: false,
            isGroup: false,
            value: 'requested',
            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
            // data: g.taskStatus
          },

        },
        <SingleCodeField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskCode',
            fieldName: "Task Code",
            fieldType: 'SingleCodeField',
            inputType: 'hidden',
            value: "fulfill",
            isHidden: true,
            isArray: false,
            isGroup: false,

            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
            // data: g.taskCode
          },

        },
        //taskName


        <GroupField>{
          generalProperties: <generalFieldsData>{
            fieldApiName: 'taskRecurrenceDateAndTime',
            fieldName: "Task Recurrence Date and Time",
            fieldType: 'SingleCodeField',

            isArray: true,
            isGroup: true,

            auth: {
              read: 'all',
              write: 'doctor, nurse'
            },
          },

          keys: ['recurrencePattern', 'oneTimeDate', 'daysOfWeek', 'daysOfMonth',
            'startMonth', 'endMonth', 'startDate', 'endDate',
            'throughoutAdmissionPeriodTime', 'startTime', 'endTime',
            'executionDate', 'executionTime', 'oneTimeTime', 'taskDuration'],
          //    [ 'isOneTime', 'isThroughOutAdmissionPeriod', 'startDateTime', 'endDateTime', 'isFortnightly',
          groupFields: {
            'recurrencePattern': <SingleCodeField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'recurrencePattern',
                fieldName: "Recurrence Pattern",
                fieldType: 'SingleCodeField',
                inputType: 'select',
                isArray: false,
                isGroup: false,

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
                controllingField: [{
                  isAControlField: true,
                  dependentFieldVisibilityTriggerValue: 'One Time',
                  controlledFieldDependencyId: 'taskRecurrenceDateAndTime.oneTime'
                },
                {
                  isAControlField: true,
                  dependentFieldVisibilityTriggerValue: 'Daily',
                  controlledFieldDependencyId: 'taskRecurrenceDateAndTime.daily'
                },
                {
                  isAControlField: true,
                  dependentFieldVisibilityTriggerValue: 'Weekly',
                  controlledFieldDependencyId: 'taskRecurrenceDateAndTime.weekly'
                },
                {
                  isAControlField: true,
                  dependentFieldVisibilityTriggerValue: 'Monthly',
                  controlledFieldDependencyId: 'taskRecurrenceDateAndTime.monthly'
                },
                {
                  isAControlField: true,
                  dependentFieldVisibilityTriggerValue: 'Yearly',
                  controlledFieldDependencyId: 'taskRecurrenceDateAndTime.yearly'
                },
                {
                  isAControlField: true,
                  dependentFieldVisibilityTriggerValue: 'Daily Throughout Admission Period',
                  controlledFieldDependencyId: 'taskRecurrenceDateAndTime.throughoutAdmissionPeriod'
                }

                ],
              },
              data: ['One Time', 'Daily', 'Weekly', 'Monthly']
            },
            'daysOfWeek': <SingleCodeField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'daysOfWeek',
                fieldName: "Days of Week",
                fieldType: 'SingleCodeField',
                inputType: 'select',
                isArray: false,
                isGroup: false,
                dependence_id: 'taskRecurrenceDateAndTime.weekly',
                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },
              data: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
            },
            'daysOfMonth': <SingleCodeField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'daysOfMonth',
                fieldName: "Days of Month",
                fieldType: 'SingleCodeField',
                inputType: 'select',
                isArray: false,
                isGroup: false,
                dependence_id: 'taskRecurrenceDateAndTime.monthly',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },
              data: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10',

              ],
            },
            'startDate': <IndividualField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'startDate',
                fieldName: "Start Date",
                fieldType: 'IndividualField',
                inputType: 'datetime-local',
                isArray: false,
                isGroup: false,
                dependence_id: 'taskRecurrenceDateAndTime.daily, taskRecurrenceDateAndTime.monthly, taskRecurrenceDateAndTime.yearly',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },
            },
            'startTime': <IndividualField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'startTime',
                fieldName: "Start Time",
                fieldType: 'IndividualField',
                inputType: 'time',
                isArray: false,
                isGroup: false,
                dependence_id: 'taskRecurrenceDateAndTime.daily, taskRecurrenceDateAndTime.monthly, taskRecurrenceDateAndTime.yearly',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },
            },
            'endDate': <IndividualField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'endDate',
                fieldName: "End Date",
                fieldType: 'IndividualField',
                inputType: 'datetime-local',
                isArray: false,
                isGroup: false,
                dependence_id: 'taskRecurrenceDateAndTime.daily, taskRecurrenceDateAndTime.monthly, taskRecurrenceDateAndTime.yearly',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },
            },
            'endTime': <IndividualField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'endTime',
                fieldName: "End Time",
                fieldType: 'IndividualField',
                inputType: 'time',
                isArray: false,
                isGroup: false,
                dependence_id: 'taskRecurrenceDateAndTime.daily, taskRecurrenceDateAndTime.monthly, taskRecurrenceDateAndTime.yearly',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },
            },
            'oneTimeDate': <IndividualField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'oneTimeDate',
                fieldName: "Date",
                fieldType: 'IndividualField',
                inputType: 'datetime-local',
                isArray: false,
                isGroup: false,
                dependence_id: 'taskRecurrenceDateAndTime.oneTime',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },

            },
            'oneTimeTime': <IndividualField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'oneTimeTime',
                fieldName: "Time",
                fieldType: 'IndividualField',
                inputType: 'time',
                isArray: false,
                isGroup: false,
                dependence_id: 'taskRecurrenceDateAndTime.oneTime',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },

            },
            'throughoutAdmissionPeriodTime': <SingleCodeField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'throughoutAdmissionPeriodTime',
                fieldName: "Daily Time",
                fieldType: 'IndividualField',
                inputType: 'datetime-local',
                isArray: false,
                isGroup: false,
                value: 'true',
                dependence_id: 'taskRecurrenceDateAndTime.throughoutAdmissionPeriod',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },

            },
            'taskDuration': <IndividualField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'taskDuration',
                fieldName: "Expected Duration",
                fieldLabel: "How long will this task take?",
                fieldType: 'IndividualField',
                inputType: 'text',
                isArray: false,
                isGroup: false,
                placeholder: 'e.g., 15 minutes, 1 hour, 30 mins',

                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },

            },

          }

        }

      ]
      //   };
      this.overallTasksForm = this.fb.group({
        tasksForm: this.fb.array([
          this.fb.group({
            taskName: this.fb.control(''),
            owner: this.fb.control(''),
            taskDescription: this.fb.control(''),
            taskFocus: this.fb.control(''),
            taskIntent: this.fb.control('order'),
            taskPriority: this.fb.control(''),
            taskStatus: this.fb.control('requested'),
            taskCode: this.fb.control('fulfill'),
            // taskRecurrenceDateAndTime: this.fb.group({
            recurrencePattern: this.fb.control(''),
            daysOfWeek: this.fb.control([]),
            daysOfMonth: this.fb.control([]),
            oneTimeDate: this.fb.control(''),
            // Convert oneTimeTime to FormArray to support multiple times per task
            oneTimeTime: this.fb.array([
              this.createTimeEntryGroup()
            ]),
            throughoutAdmissionPeriodTime: this.fb.control(''),
            startDate: this.fb.control(''),
            endDate: this.fb.control(''),
            startTime: this.fb.control(''),
            endTime: this.fb.control(''),
            startMonth: this.fb.control(''),
            endMonth: this.fb.control(''),
            executionDate: this.fb.control(''),
            executionTime: this.fb.control(''),
            taskDuration: this.fb.control(''),
            // Remove these as they'll be per time entry now
            // isPeriodInstead: this.fb.control(false),
            // isTimeRangeInstead: this.fb.control(false),
            // isAnyTimeInstead: this.fb.control(false),
            // isOneTimeInstead: this.fb.control(true),
            // })
          })
        ])
      })

      // Initialize formFieldsToUse for care plan form
      // This should match the structure from add-admission component
      this.formFieldsToUse = {
        carePlan: {
          formFields: this.g?.carePlanFormFields || [] // Load from test data if available
        }
      };
    }
    );
  }

  /**
   * Creates a single time entry group with its own mode flags
   */
  createTimeEntryGroup(): FormGroup {
    return this.fb.group({
      timeValue: this.fb.control(''), // Stores the actual time value
      isPeriodInstead: this.fb.control(false),
      isTimeRangeInstead: this.fb.control(false),
      isAnyTimeInstead: this.fb.control(false),
      isOneTimeInstead: this.fb.control(true),
    });
  }

  /**
   * Get the oneTimeTime FormArray for a specific task
   */
  getTimeEntriesArray(taskIndex: number): FormArray {
    return this.taskFormArray.at(taskIndex).get(['oneTimeTime']) as FormArray;
  }

  /**
   * Add a new time entry to a task
   */
  addTimeEntry(taskIndex: number) {
    const timeArray = this.getTimeEntriesArray(taskIndex);
    timeArray.push(this.createTimeEntryGroup());

    setTimeout(() => {
      this.cd.detectChanges();
    }, 0);
  }

  /**
   * Remove a time entry from a task
   */
  removeTimeEntry(taskIndex: number, timeIndex: number) {
    const timeArray = this.getTimeEntriesArray(taskIndex);
    if (timeArray.length > 1) {
      timeArray.removeAt(timeIndex);
    }

    setTimeout(() => {
      this.cd.detectChanges();
    }, 0);
  }

  /**
   * Check if "Any Time" option should be available
   * Only available when there's exactly one time entry
   */
  canUseAnyTime(taskIndex: number): boolean {
    const timeArray = this.getTimeEntriesArray(taskIndex);
    return timeArray.length === 1;
  }
  overallTasksForm?: FormGroup
  cd = inject(ChangeDetectorRef);

  /**
   * Set the time mode for a specific time entry
   */
  setTimeMode(timeEntryGroup: FormGroup, fieldName: string) {
    ['isTimeRangeInstead', 'isPeriodInstead', 'isOneTimeInstead', 'isAnyTimeInstead'].forEach((field) => {
      timeEntryGroup.get([field])?.setValue(false);
    });
    timeEntryGroup.get([fieldName])?.setValue(true);

    // Set the appropriate value for timeValue based on which option is selected
    if (fieldName === 'isAnyTimeInstead') {
      timeEntryGroup.get(['timeValue'])?.setValue('Any Time During The Day');
      timeEntryGroup.get(['timeValue'])?.disable(); // Disable the control
    } else if (fieldName === 'isOneTimeInstead' || fieldName === 'isTimeRangeInstead') {
      // Clear the value for time picker or time range (will be set by user input)
      timeEntryGroup.get(['timeValue'])?.setValue('');
      timeEntryGroup.get(['timeValue'])?.enable(); // Enable the control
    } else if (fieldName === 'isPeriodInstead') {
      timeEntryGroup.get(['timeValue'])?.enable(); // Enable the control for period selection
    }

    setTimeout(() => {
      this.cd.detectChanges();
    }, 0);
  }

  // Keep old method for backward compatibility, but mark as deprecated
  setToAnyTime(taski: FormGroup, fieldName: string) {
    ['isTimeRangeInstead',
      'isPeriodInstead', 'isOneTimeInstead', 'isAnyTimeInstead'

    ].forEach((field) => {
      taski.get([field])?.setValue(false);
    })
    taski.get([fieldName])?.setValue(true);

    // Set the appropriate value for oneTimeTime based on which option is selected
    if (fieldName === 'isAnyTimeInstead') {
      taski.get(['oneTimeTime'])?.setValue('Any Time During The Day');
      taski.get(['oneTimeTime'])?.disable(); // Disable the control
    } else if (fieldName === 'isOneTimeInstead' || fieldName === 'isTimeRangeInstead') {
      // Clear the value for time picker or time range (will be set by user input)
      taski.get(['oneTimeTime'])?.setValue('');
      taski.get(['oneTimeTime'])?.enable(); // Enable the control
    } else if (fieldName === 'isPeriodInstead') {
      taski.get(['oneTimeTime'])?.enable(); // Enable the control for period selection
    }

    setTimeout(() => {

      this.cd.detectChanges();
    }, 0);

  }

  /**
   * Updates the timeValue when the 'from' time changes in a time range
   */
  updateTimeRangeFrom(timeEntryGroup: FormGroup, newFromValue: string) {
    const isoValue = newFromValue ? new Date(newFromValue).toISOString() : newFromValue;
    console.log('updateTimeRangeFrom called with:', isoValue);
    const currentToValue = this.getTimeRangeTo(timeEntryGroup);
    console.log('Current To value:', currentToValue);

    if (isoValue && currentToValue) {
      const rangeValue = `${isoValue}$#$${currentToValue}`;
      console.log('Setting range value:', rangeValue);
      timeEntryGroup.get(['timeValue'])?.setValue(rangeValue);
    } else if (isoValue) {
      // Store just the from value temporarily
      console.log('Only from value, storing temporarily');
      timeEntryGroup.get(['timeValue'])?.setValue(`${isoValue}$#$`);
    }
  }

  /**
   * Updates the timeValue when the 'to' time changes in a time range
   */
  updateTimeRangeTo(timeEntryGroup: FormGroup, newToValue: string) {
    const isoValue = newToValue ? new Date(newToValue).toISOString() : newToValue;
    console.log('updateTimeRangeTo called with:', isoValue);
    const currentFromValue = this.getTimeRangeFrom(timeEntryGroup);
    console.log('Current From value:', currentFromValue);

    if (currentFromValue && isoValue) {
      const rangeValue = `${currentFromValue}$#$${isoValue}`;
      console.log('Setting range value:', rangeValue);
      timeEntryGroup.get(['timeValue'])?.setValue(rangeValue);
    } else if (isoValue) {
      // Store just the to value temporarily
      console.log('Only to value, storing temporarily');
      timeEntryGroup.get(['timeValue'])?.setValue(`$#$${isoValue}`);
    }
  }

  /**
   * Gets the 'from' time from the timeValue (format: "ISO$#$ISO")
   */
  getTimeRangeFrom(timeEntryGroup: FormGroup): string {
    const value = timeEntryGroup.get(['timeValue'])?.value || '';
    console.log('getTimeRangeFrom - raw value:', value);
    if (value && value.includes('$#$')) {
      const fromTime = value.split('$#$')[0];
      console.log('getTimeRangeFrom - returning:', fromTime);
      return fromTime;
    }
    console.log('getTimeRangeFrom - returning empty');
    return '';
  }

  /**
   * Gets the 'to' time from the timeValue (format: "ISO$#$ISO")
   */
  getTimeRangeTo(timeEntryGroup: FormGroup): string {
    const value = timeEntryGroup.get(['timeValue'])?.value || '';
    console.log('getTimeRangeTo - raw value:', value);
    if (value && value.includes('$#$')) {
      const toTime = value.split('$#$')[1];
      console.log('getTimeRangeTo - returning:', toTime);
      return toTime;
    }
    console.log('getTimeRangeTo - returning empty');
    return '';
  }

  addTasks() {
    this.taskFormArray.push(this.fb.group({
      taskName: this.fb.control(''),
      taskDescription: this.fb.control(''),
      taskFocus: this.fb.control(''),
      taskIntent: this.fb.control('order'),
      taskPriority: this.fb.control(''),
      taskStatus: this.fb.control('requested'),
      taskCode: this.fb.control('fulfill'),
      // taskRecurrenceDateAndTime: this.fb.group({
      recurrencePattern: this.fb.control(''),
      daysOfWeek: this.fb.control([]),
      daysOfMonth: this.fb.control([]),
      oneTimeDate: this.fb.control(''),
      oneTimeTime: this.fb.array([
        this.createTimeEntryGroup()
      ]),
      throughoutAdmissionPeriodTime: this.fb.control(''),
      startDate: this.fb.control(''),
      endDate: this.fb.control(''),
      startTime: this.fb.control(''),
      endTime: this.fb.control(''),
      startMonth: this.fb.control(''),
      endMonth: this.fb.control(''),
      executionDate: this.fb.control(''),
      executionTime: this.fb.control(''),
      taskDuration: this.fb.control(''),
      // Removed - these are now per time entry
      // isPeriodInstead: this.fb.control(false),
      // isTimeRangeInstead: this.fb.control(false),
      // isAnyTimeInstead: this.fb.control(false),
      // isOneTimeInstead: this.fb.control(true),
      // })
    }));

    setTimeout(() => {
      this.cd.detectChanges();
    }, 0)
  }

  /**
   * Validates the entire tasks form before submission
   * Returns an object with isValid boolean and errors array
   */
  validateTasksForm(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const tasksArray = this.taskFormArray;

    if (!tasksArray || tasksArray.length === 0) {
      errors.push('At least one task is required.');
      return { isValid: false, errors };
    }

    // Validate each task
    tasksArray.controls.forEach((taskControl, taskIndex) => {
      const task = taskControl as FormGroup;
      const taskNumber = taskIndex + 1;
      const recurrencePattern = task.get(['recurrencePattern'])?.value?.trim();

      // Basic field validation
      const taskName = task.get(['taskName'])?.value?.trim();
      if (!taskName) {
        errors.push(`Task ${taskNumber}: Task name is required.`);
      }

      if (!recurrencePattern) {
        errors.push(`Task ${taskNumber}: Recurrence pattern is required.`);
        return; // Skip further validation if no recurrence pattern
      }

      // Step 1: Validate required fields based on recurrence pattern
      switch (recurrencePattern) {
        case 'One Time':
          const oneTimeDate = task.get(['oneTimeDate'])?.value;
          if (!oneTimeDate) {
            errors.push(`Task ${taskNumber}: Date is required for "One Time" tasks.`);
          }
          break;

        case 'Weekly':
          const daysOfWeek = task.get(['daysOfWeek'])?.value;
          const weeklyStartDate = task.get(['startDate'])?.value;
          const weeklyEndDate = task.get(['endDate'])?.value;

          if (!daysOfWeek || (Array.isArray(daysOfWeek) && daysOfWeek.length === 0)) {
            errors.push(`Task ${taskNumber}: Day of week is required for "Weekly" tasks.`);
          }
          if (!weeklyStartDate) {
            errors.push(`Task ${taskNumber}: Start date is required for "Weekly" tasks.`);
          }
          if (!weeklyEndDate) {
            errors.push(`Task ${taskNumber}: End date is required for "Weekly" tasks.`);
          }
          break;

        case 'Monthly':
          const daysOfMonth = task.get(['daysOfMonth'])?.value;
          const startMonth = task.get(['startMonth'])?.value;
          const endMonth = task.get(['endMonth'])?.value;

          if (!daysOfMonth || (Array.isArray(daysOfMonth) && daysOfMonth.length === 0)) {
            errors.push(`Task ${taskNumber}: Day of month is required for "Monthly" tasks.`);
          }
          if (!startMonth) {
            errors.push(`Task ${taskNumber}: Start month is required for "Monthly" tasks.`);
          }
          if (!endMonth) {
            errors.push(`Task ${taskNumber}: End month is required for "Monthly" tasks.`);
          }
          break;

        case 'Daily':
          const dailyStartDate = task.get(['startDate'])?.value;
          const dailyEndDate = task.get(['endDate'])?.value;

          if (!dailyStartDate) {
            errors.push(`Task ${taskNumber}: Start date is required for "Daily" tasks.`);
          }
          if (!dailyEndDate) {
            errors.push(`Task ${taskNumber}: End date is required for "Daily" tasks.`);
          }
          break;

        case 'Daily Throughout Admission Period':
          const admissionTime = task.get(['throughoutAdmissionPeriodTime'])?.value;
          if (!admissionTime) {
            errors.push(`Task ${taskNumber}: Time is required for "Daily Throughout Admission Period" tasks.`);
          }
          break;
      }

      // Step 2: Validate time entries array
      const timeEntriesArray = task.get(['oneTimeTime']) as FormArray;

      if (!timeEntriesArray || timeEntriesArray.length === 0) {
        errors.push(`Task ${taskNumber}: At least one time entry is required.`);
      } else {
        // Validate each time entry
        timeEntriesArray.controls.forEach((timeEntryControl, timeIndex) => {
          const timeEntry = timeEntryControl as FormGroup;
          const timeNumber = timeIndex + 1;
          const timeValue = timeEntry.get(['timeValue'])?.value;
          const isAnyTime = timeEntry.get(['isAnyTimeInstead'])?.value;

          // Skip validation for "Any Time" option
          if (isAnyTime && timeValue === 'Any Time During The Day') {
            return;
          }

          // Check if timeValue exists and is not empty
          // Handle different data types (string, Date object, etc.)
          const isEmpty = !timeValue ||
            (typeof timeValue === 'string' && timeValue.trim() === '') ||
            (typeof timeValue === 'object' && timeValue === null);

          if (isEmpty) {
            errors.push(`Task ${taskNumber}, Time ${timeNumber}: Time value is required.`);
          } else {
            // Additional validation based on time mode
            const isTimeRange = timeEntry.get(['isTimeRangeInstead'])?.value;
            const timeValueStr = typeof timeValue === 'string' ? timeValue : String(timeValue);

            if (isTimeRange) {
              // Time range should have the format "ISO$#$ISO"
              if (!timeValueStr.includes('$#$')) {
                errors.push(`Task ${taskNumber}, Time ${timeNumber}: Invalid time range format.`);
              } else {
                const [fromTime, toTime] = timeValueStr.split('$#$');
                if (!fromTime || !toTime) {
                  errors.push(`Task ${taskNumber}, Time ${timeNumber}: Both "from" and "to" times are required for time range.`);
                }
              }
            }
          }
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: any, @Optional() public dref: MatDialogRef<TestingTasksComponent>) {


  }

  /**
   * Fetch active service requests for the current patient
   */
  private async fetchActiveServiceRequests(): Promise<any[]> {
    const patientId = await firstValueFrom(this.stateService.currentPatientId$);

    if (!patientId) {
      console.error('No patient ID available');
      return [];
    }

    this.loadingServiceRequests = true;

    try {
      const url = `${this.backendUrl}/ServiceRequest?patient=${patientId}&status=active&_sort=-_lastUpdated`;
      const bundle: any = await firstValueFrom(this.http.get(url));

      const serviceRequests = bundle?.entry?.map((entry: any) => entry.resource) || [];
      console.log('Fetched active service requests:', serviceRequests);

      this.loadingServiceRequests = false;
      return serviceRequests;
    } catch (error) {
      console.error('Error fetching service requests:', error);
      this.loadingServiceRequests = false;
      return [];
    }
  }

  /**
   * Opens a menu to choose between selecting existing service requests or creating a new one
   * @param taskIndex - The index of the task to add the service request to
   */
  async openTaskDetailsMenu(taskIndex: number, event: MouseEvent) {
    // Prevent default button behavior
    event.stopPropagation();

    // Fetch active service requests
    this.existingServiceRequests = await this.fetchActiveServiceRequests();

    // Create menu options based on available service requests
    const hasExistingRequests = this.existingServiceRequests.length > 0;
    this.openSelectExistingServiceRequest(taskIndex);
    // For now, we'll just show which option would be available
    // You can implement a proper menu component or dialog
    console.log('Has existing service requests:', hasExistingRequests);
    console.log('Existing requests count:', this.existingServiceRequests.length);
  }

  /**
   * Opens dialog to select from existing active service requests
   * @param taskIndex - The index of the task
   */
  openSelectExistingServiceRequest(taskIndex: number) {
    if (this.existingServiceRequests.length === 0) {
      this.errorService.openandCloseError('No active lab tests or procedures found for this patient.');
      return;
    }

    // Transform service requests into reference field format
    const serviceRequestReferences = this.existingServiceRequests.map(sr => ({
      reference: `ServiceRequest/${sr.id}`,
      display: sr.code?.text || sr.code?.coding?.[0]?.display || `Service Request ${sr.id}`,
      resource: sr // Include full resource
    }));

    const dialogRef = this.dialog.open(DynamicFormsV2Component, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        formFields: [
          <IndividualReferenceField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'serviceRequest',
              fieldName: "Select Lab Test or Procedure",
              fieldLabel: "Choose from existing orders",
              fieldType: 'IndividualReferenceField',
              inputType: 'select',
              isArray: false,
              isGroup: false,
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
            },
            data: serviceRequestReferences
          }
        ],
        formMetaData: {
          formName: 'Select Existing Order',
          formDescription: 'Choose from active lab tests or procedures for this patient',
          closeDialogOnSubmit: true
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.values?.serviceRequest) {
        const selectedRef = result.values.serviceRequest;

        // Find the full service request resource
        const selectedSR = this.existingServiceRequests.find(sr =>
          `ServiceRequest/${sr.id}` === selectedRef || selectedRef.reference === `ServiceRequest/${sr.id}`
        );

        if (selectedSR) {
          // Store the reference in the task
          const task = this.taskFormArray.at(taskIndex);
          task.get(['taskFocus'])?.setValue(selectedRef);

          // Extract and store service request details
          const srId = selectedSR.id || '';
          const srCode = selectedSR.code?.coding?.[0]?.code || selectedSR.code?.text || 'N/A';
          const srDisplay = selectedSR.code?.text || selectedSR.code?.coding?.[0]?.display || `Service Request ${srId}`;

          this.taskServiceRequests.set(taskIndex, {
            id: srId,
            code: srCode,
            display: srDisplay,
            resource: selectedSR
          });

          console.log('Selected service request for task', taskIndex, this.taskServiceRequests.get(taskIndex));
        }
      }
    });
  }

  /**
   * Opens the lab request dialog to add a new service request
   * @param taskIndex - The index of the task to add the service request to
   */
  ecServ = inject(EncounterServiceService);
  async openAddNewServiceRequest(taskIndex: number) {
    const patientId = await firstValueFrom(this.stateService.currentPatientId$);

    // Snapshot existing IDs before opening dialog
    const existingList = this.stateService.PatientResources.serviceRequests.getValue() || [];
    const existingIds = new Set(
      existingList
        .map(sr => sr?.actualResource?.id || sr?.referenceId)
        .filter(Boolean)
    );

    // Open the service request dialog
    this.ecServ.addServiceRequest(patientId, null, true);

    // Subscribe to service requests changes to detect new creation
    const subscription = this.stateService.PatientResources.serviceRequests.subscribe(serviceRequests => {
      // Resolve newly created resource by finding ID not present in initial snapshot
      const newEntry = (serviceRequests || []).find(sr => {
        const id = sr?.actualResource?.id || sr?.referenceId;
        return id && !existingIds.has(id);
      });

      const newSR = newEntry?.actualResource || (serviceRequests.length > existingIds.size ? serviceRequests[0]?.actualResource : null);

      if (newSR?.id) {
        // Store the reference in the task
        const task = this.taskFormArray.at(taskIndex);
        task.get(['taskFocus'])?.setValue(`ServiceRequest/${newSR.id}`);

        // Extract and store service request details
        const srId = newSR.id;
        const srCode = (newSR as any).code?.coding?.[0]?.code || (newSR as any).code?.text || 'N/A';
        const srDisplay = (newSR as any).code?.text || (newSR as any).code?.coding?.[0]?.display || `Service Request ${srId}`;

        this.taskServiceRequests.set(taskIndex, {
          id: srId,
          code: srCode,
          display: srDisplay,
          resource: newSR
        });

        console.log('Created new service request for task', taskIndex, this.taskServiceRequests.get(taskIndex));

        // Unsubscribe after detecting the change
        subscription.unsubscribe();
      }
    });

    const dialogSub = this.dialog.afterAllClosed.subscribe(() => {
      setTimeout(() => {
        subscription.unsubscribe();
        dialogSub.unsubscribe();
      }, 3000);
    });
  }

  /**
   * Get service request details for a task
   */
  getTaskServiceRequest(taskIndex: number): { id: string; code: string; display: string } | null {
    return this.taskServiceRequests.get(taskIndex) || null;
  }

  /**
   * Check if a task has a linked service request
   */
  hasServiceRequest(taskIndex: number): boolean {
    return this.taskServiceRequests.has(taskIndex);
  }

  /**
   * Remove service request link from a task
   */
  removeTaskServiceRequest(taskIndex: number) {
    this.taskServiceRequests.delete(taskIndex);
    const task = this.taskFormArray.at(taskIndex);
    task.get(['taskFocus'])?.setValue('');
  }

  /**
   * Set the focus field for a task
   */
  setTaskFocus(taskIndex: number, focusValue: string) {
    const task = this.taskFormArray.at(taskIndex);
    task.get(['taskFocus'])?.setValue(focusValue);
  }

  /**
   * Clear the focus field for a task
   */
  clearTaskFocus(taskIndex: number) {
    const task = this.taskFormArray.at(taskIndex);
    task.get(['taskFocus'])?.setValue('');
  }

  /**
   * Build the two assignee pools from the (interceptor-scoped) PractitionerRole bundle.
   * Roles keep a representative PractitionerRole/{id} reference so the existing
   * "Role Tasks" query (owner=PractitionerRole/{id}) still resolves.
   */
  private buildOwnerOptions(practitionerOptions: any[], practitionerRoleEntries: any[]): void {
    const nameByRef = new Map<string, string>();
    (practitionerOptions || []).forEach((p: any) => {
      if (p?.reference) nameByRef.set(p.reference, p.display);
    });

    const roleMap = new Map<string, OwnerOption>();
    const pracMap = new Map<string, OwnerOption>();

    for (const entry of practitionerRoleEntries || []) {
      const r = entry?.resource;
      if (!r || r.resourceType !== 'PractitionerRole') continue;

      const rawCode = r.code?.[0]?.coding?.[0]?.code || r.code?.[0]?.text || '';
      const roleCode = normalizeRole(rawCode);
      if (!roleCode) continue;
      // Remote doctors are not assignable to tasks — skip as a role and as a practitioner role.
      if (EXCLUDED_OWNER_ROLE_CODES.has(roleCode)) continue;

      if (!roleMap.has(roleCode)) {
        roleMap.set(roleCode, {
          kind: 'role',
          reference: `PractitionerRole/${r.id}`,
          display: ROLE_DISPLAY_NAMES[roleCode as RoleType] || rawCode,
          type: 'PractitionerRole',
          roleCodes: [roleCode]
        });
      }

      const pracRef: string | undefined = r.practitioner?.reference;
      if (!pracRef) continue;

      const existing = pracMap.get(pracRef);
      if (existing) {
        if (!existing.roleCodes.includes(roleCode)) existing.roleCodes.push(roleCode);
      } else {
        pracMap.set(pracRef, {
          kind: 'practitioner',
          reference: pracRef,
          display: nameByRef.get(pracRef) || pracRef.replace('Practitioner/', ''),
          type: 'Practitioner',
          roleCodes: [roleCode]
        });
      }
    }

    this.ownerRoleOptions = Array.from(roleMap.values());
    this.ownerPractitionerOptions = Array.from(pracMap.values());
  }

  /**
   * Roles eligible to execute a task, derived from its focus. Returns null when there is
   * no focus yet (no filtering). Source of truth = capacityObject execution-role lists.
   */
  private getEligibleRolesForTask(taskIndex: number): Set<string> | null {
    const focus = this.taskServiceRequests.get(taskIndex);
    if (!focus) return null;

    const resource = focus.resource;
    const isMedication =
      resource?.resourceType === 'MedicationRequest' ||
      (focus.display || '').toLowerCase().includes('medication');

    let roles: string[];
    if (isMedication) {
      roles = [
        ...(capacityObject['medicationRequest']?.['dispense'] || []),
        ...(capacityObject['medicationRequest']?.['administer'] || [])
      ];
    } else {
      const category = (resource?.category?.[0]?.coding?.[0]?.code
        || resource?.category?.[0]?.text || '').toString().toLowerCase().trim();
      const isLab = category === 'laboratory' || category === 'lab';
      roles = isLab
        ? (capacityObject['labRequest']?.['fulfill'] || [])
        : (capacityObject['serviceRequest']?.['fulfill'] || []);
    }

    return new Set(roles.map(r => normalizeRole(r)).filter(Boolean));
  }

  getOwnerRoleOptions(taskIndex: number): OwnerOption[] {
    return this.withEligibility(this.ownerRoleOptions, taskIndex);
  }

  getOwnerPractitionerOptions(taskIndex: number): OwnerOption[] {
    return this.withEligibility(this.ownerPractitionerOptions, taskIndex);
  }

  private withEligibility(options: OwnerOption[], taskIndex: number): OwnerOption[] {
    const eligible = this.getEligibleRolesForTask(taskIndex);
    return options.map(opt => ({
      ...opt,
      eligible: !eligible || opt.roleCodes.some(rc => eligible.has(rc))
    }));
  }

  compareOwnerOptions(a: any, b: any): boolean {
    return !!a && !!b && a.reference === b.reference;
  }

  /**
   * Submit the task form data to the server
   */
  submitTaskForm() {
    // Prepare the data for submission
    const formData = this.overallTasksForm?.value;

    // For each task, include the selected service request details if available
    formData.tasksForm.forEach((task: any, index: number) => {
      const serviceRequest = this.taskServiceRequests.get(index);
      if (serviceRequest) {
        task.taskFocus = serviceRequest;
      }
    });

    this.http.post(`http://localhost:3000/api/tasks/submit`, formData).subscribe({
      next: (res) => {
        this.taskSubmissionResult = res;
        if (this.dref) {
          this.dref.close(this.taskSubmissionResult);
        }
      },
      error: (err) => {
        console.error('Submission error:', err);
        this.errorService.openandCloseError('Failed to submit tasks. Please try again.');
      }
    });
  }
  taskSubmissionResult: any;

  // private carePlanDialogService = inject(CarePlanDialogService);

  /**
   * Open care plan form to add/edit care plan with tasks enabled
   */
  // openCarePlanForm(carePlanId?: string) {
  //   this.carePlanDialogService.openCarePlanDialog(carePlanId, true);
  // }

  // Constants
  private readonly MAX_TASK_WARNING_THRESHOLD = 100;
  private readonly PERIOD_TIME_RANGES = {
    'morning': { start: 6, end: 12 },
    'afternoon': { start: 12, end: 18 },
    'evening': { start: 18, end: 21 },
    'night': { start: 21, end: 24 },
    'midnight': { start: 0, end: 6 }
  };

  /**
   * Main task submission handler - validates and generates all tasks
   */
  saveTasks() {
    // Validate the form before submission
    const validation = this.validateTasksForm();

    if (!validation.isValid) {
      const errorList = validation.errors.map((err, idx) => `${idx + 1}. ${err}`).join('\n');
      const errorMessage = `Please fix the following errors:\n\n${errorList}`;
      this.errorService.openandCloseError(errorMessage);
      console.error('Validation errors:', validation.errors);
      return;
    }

    // Generate all tasks from form data
    this.generateAndSubmitTasks();
  }

  /**
   * MAIN GENERATION LOGIC
   * Generates all FHIR Task resources from form data and submits via bundle transaction
   */
  cloneFocusResource(originalResource: any): any {
    if (!originalResource) return null;
    const cloned = JSON.parse(JSON.stringify(originalResource));
    delete cloned.id;
    delete cloned.meta;

    // Surgically remove only the backend business identifier (e.g. SR-..., MR-...),
    // preserving any third-party or custom identifiers.
    const resType = cloned.resourceType;
    const abbr = resType === 'ServiceRequest' ? 'sr'
      : resType === 'MedicationRequest' ? 'mr'
      : resType ? resType.substring(0, Math.min(3, resType.length)).toLowerCase() : '';
    const businessSystemUrl = `${this.terminologyBaseUrl}/fhir/identifier/${abbr}`;
    const businessPrefix = `${abbr.toUpperCase()}-`;

    if (Array.isArray(cloned.identifier)) {
      cloned.identifier = cloned.identifier.filter((id: any) => {
        const matchesSystem = id?.system === businessSystemUrl;
        const matchesPrefix = typeof id?.value === 'string' && id.value.toUpperCase().startsWith(businessPrefix);
        return !(matchesSystem || matchesPrefix);
      });
      if (cloned.identifier.length === 0) {
        delete cloned.identifier;
      }
    }

    cloned.status = 'active';

    if (cloned.resourceType === 'ServiceRequest') {
      let reqResourceType: 'lab' | 'service' | 'imaging' | 'procedure' = 'lab';
      const catCode = (cloned.category?.[0]?.coding?.[0]?.code || cloned.category?.[0]?.text || '').toLowerCase();
      if (catCode.includes('imaging') || catCode.includes('radiology')) {
        reqResourceType = 'imaging';
      } else if (catCode.includes('procedure')) {
        reqResourceType = 'procedure';
      } else if (catCode.includes('lab')) {
        reqResourceType = 'lab';
      } else {
        reqResourceType = 'service';
      }

      cloned.requisition = this.utilityService.generateRequisition({
        prefix: 'REQ',
        resourceType: reqResourceType,
        system: cloned.requisition?.system || `${this.backendUrl}`
      });
      delete cloned.groupIdentifier;
      cloned.authoredOn = new Date().toISOString();
    } else if (cloned.resourceType === 'MedicationRequest') {
      cloned.groupIdentifier = this.utilityService.generateRequisition({
        prefix: 'MR',
        resourceType: 'medReq',
        system: cloned.groupIdentifier?.system || `${this.backendUrl}`
      });
      delete cloned.requisition;
      cloned.authoredOn = new Date().toISOString();
    }

    return cloned;
  }

  private async generateAndSubmitTasks(): Promise<void> {
    try {
      const patientId = await firstValueFrom(this.stateService.currentPatientId$);
      if (!patientId) {
        this.errorService.openandCloseError('No patient selected');
        return;
      }

      const formData = this.overallTasksForm?.value;
      if (!formData?.tasksForm || formData.tasksForm.length === 0) {
        this.errorService.openandCloseError('No tasks to submit');
        return;
      }

      // Fetch existing tasks for duplicate detection
      const existingTasks = await this.fetchExistingTasks(patientId);

      let allGeneratedTasks: any[] = [];
      const skippedDuplicates: string[] = [];
      const extraBundleEntries: any[] = [];

      // Generate tasks for each form element
      for (let configIndex = 0; configIndex < formData.tasksForm.length; configIndex++) {
        const taskConfig = formData.tasksForm[configIndex];
        const groupIdentifier = this.generateGroupIdentifier(configIndex);
        const dates = this.generateDateList(taskConfig);
        const serviceRequestDetails = this.taskServiceRequests.get(configIndex);

        let occurrenceCount = 0;

        for (const date of dates) {
          const timeEntries = taskConfig.oneTimeTime || [];

          for (const timeEntry of timeEntries) {
            occurrenceCount++;

            let currentServiceRequestDetails = serviceRequestDetails;

            // Clone the resource for occurrences > 1
            if (serviceRequestDetails?.resource && occurrenceCount > 1) {
              const clonedResource = this.cloneFocusResource(serviceRequestDetails.resource);
              const clonedUuid = `urn:uuid:${crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e12).toString()}`;

              extraBundleEntries.push({
                fullUrl: clonedUuid,
                resource: clonedResource,
                request: {
                  method: 'POST',
                  url: clonedResource.resourceType
                }
              });

              currentServiceRequestDetails = {
                id: clonedUuid,
                code: serviceRequestDetails.code,
                display: serviceRequestDetails.display
              };

              // If it's a ServiceRequest, generate its billing (ChargeItem + Invoice + Observation/Procedure/ImagingStudy)
              if (clonedResource.resourceType === 'ServiceRequest') {
                try {
                  const billingResult = await firstValueFrom(
                    this.ecServ.prepareChargeItemAndInvoiceForServiceRequest(clonedResource, clonedUuid)
                  );
                  if (billingResult) {
                    if (billingResult.chargeItemEntry) {
                      extraBundleEntries.push(billingResult.chargeItemEntry);
                    }
                    if (billingResult.invoiceEntry) {
                      extraBundleEntries.push(billingResult.invoiceEntry);
                    }
                    if (billingResult.serviceEntries && Array.isArray(billingResult.serviceEntries)) {
                      extraBundleEntries.push(...billingResult.serviceEntries);
                    }
                  }
                } catch (err) {
                  console.error('Error preparing billing/placeholder resources for cloned ServiceRequest:', err);
                }
              }
            }

            const task = this.buildSingleTask(
              taskConfig,
              date,
              timeEntry,
              patientId,
              groupIdentifier,
              currentServiceRequestDetails
            );

            // Check for duplicates
            if (this.isDuplicateTask(task, existingTasks, allGeneratedTasks)) {
              skippedDuplicates.push(
                `${task.description || 'Unnamed'} on ${new Date(date).toLocaleDateString()}`
              );
            } else {
              allGeneratedTasks.push(task);
            }
          }
        }
      }

      // Show warnings
      if (skippedDuplicates.length > 0) {
        console.warn('Skipped duplicate tasks:', skippedDuplicates);
        this.snackBar.open(
          `Skipped ${skippedDuplicates.length} duplicate task(s)`,
          'Dismiss',
          { duration: 4000, panelClass: 'info-snackbar' }
        );
      }

      if (allGeneratedTasks.length === 0) {
        this.errorService.openandCloseError('No new tasks to create (all duplicates)');
        return;
      }

      if (allGeneratedTasks.length > this.MAX_TASK_WARNING_THRESHOLD) {
        const proceed = confirm(
          `Warning: You are about to create ${allGeneratedTasks.length} tasks. This is a large number. Continue?`
        );
        if (!proceed) return;
      }

      // Submit via bundle transaction
      await this.submitTasksBundle(allGeneratedTasks, extraBundleEntries);

    } catch (error) {
      console.error('Error generating tasks:', error);
      this.errorService.openandCloseError('Failed to generate tasks: ' + (error as Error).message);
    }
  }

  /**
   * STEP 1: Generate date list based on recurrence pattern
   */
  private generateDateList(taskConfig: any): Date[] {
    const pattern = taskConfig.recurrencePattern?.trim();

    switch (pattern) {
      case 'One Time':
        return taskConfig.oneTimeDate ? [new Date(taskConfig.oneTimeDate)] : [];

      case 'Daily':
        return this.generateDailyDates(
          taskConfig.startDate,
          taskConfig.endDate
        );

      case 'Weekly':
        return this.generateWeeklyDates(
          taskConfig.startDate,
          taskConfig.endDate,
          taskConfig.daysOfWeek
        );

      case 'Monthly':
        return this.generateMonthlyDates(
          taskConfig.startMonth,
          taskConfig.endMonth,
          taskConfig.daysOfMonth
        );

      case 'Daily Throughout Admission Period':
        // Get admission period from current encounter
        const encounter = this.stateService.currentEncounter.value;
        const admissionStart = encounter?.period?.start;
        const admissionEnd = encounter?.period?.end || new Date().toISOString();
        return admissionStart ? this.generateDailyDates(admissionStart, admissionEnd) : [];

      default:
        console.warn('Unknown recurrence pattern:', pattern);
        return [];
    }
  }

  /**
   * Generate daily dates between start and end
   */
  private generateDailyDates(startDate: string, endDate: string): Date[] {
    const dates: Date[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }

    return dates;
  }

  /**
   * Generate weekly dates for specific days of week
   */
  private generateWeeklyDates(
    startDate: string,
    endDate: string,
    daysOfWeek: string | string[]
  ): Date[] {
    const dates: Date[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const targetDays = Array.isArray(daysOfWeek) ? daysOfWeek : [daysOfWeek];

    const dayMap: { [key: string]: number } = {
      'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
      'Thursday': 4, 'Friday': 5, 'Saturday': 6
    };

    const targetDayNumbers = targetDays.map(day => dayMap[day]).filter(n => n !== undefined);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (targetDayNumbers.includes(d.getDay())) {
        dates.push(new Date(d));
      }
    }

    return dates;
  }

  /**
   * Generate monthly dates for specific days of month
   */
  private generateMonthlyDates(
    startMonth: string,
    endMonth: string,
    daysOfMonth: string | string[]
  ): Date[] {
    const dates: Date[] = [];
    const monthMap: { [key: string]: number } = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
    };

    const startMonthNum = monthMap[startMonth];
    const endMonthNum = monthMap[endMonth];
    const currentYear = new Date().getFullYear();
    const targetDays = Array.isArray(daysOfMonth) ? daysOfMonth.map(Number) : [Number(daysOfMonth)];

    for (let month = startMonthNum; month <= endMonthNum; month++) {
      targetDays.forEach(day => {
        const date = new Date(currentYear, month, day);
        if (date.getMonth() === month) { // Valid date check
          dates.push(date);
        }
      });
    }

    return dates;
  }

  /**
   * STEP 2: Build a single FHIR Task resource
   */
  private buildSingleTask(
    taskConfig: any,
    baseDate: Date,
    timeEntry: any,
    patientId: string,
    groupIdentifier: any,
    serviceRequestDetails?: { id: string; code: string; display: string } | null
  ): any {
    const period = this.calculateExecutionPeriod(baseDate, timeEntry, taskConfig.taskDuration);

    const task: any = {
      resourceType: 'Task',
      status: taskConfig.taskStatus || 'requested',
      intent: taskConfig.taskIntent || 'order',
      code: {
        coding: [{
          system: 'http://hl7.org/fhir/CodeSystem/task-code',
          code: taskConfig.taskCode || 'fulfill',
          display: 'Fulfill the focal request'
        }]
      },
      description: taskConfig.taskDescription || taskConfig.taskName || 'Unnamed Task',
      for: { reference: `Patient/${patientId}` },
      restriction: {
        period: {
          start: period.start.toISOString(),
          end: period.end.toISOString()
        }
      },
      authoredOn: new Date().toISOString(),
      groupIdentifier: groupIdentifier
    };

    if (taskConfig.owner) {
      task.owner = {
        reference: taskConfig.owner.reference || taskConfig.owner,
        display: taskConfig.owner.display || undefined
      };
    }

    const currentUser = this.auth.getCurrentUser();
    if (currentUser?.fhirResourceId) {
      task.requester = { reference: `Practitioner/${currentUser.fhirResourceId}` };
    }

    // Link to CarePlan if available
    if (this.carePlanId) {
      task.basedOn = [{ reference: `CarePlan/${this.carePlanId}` }];
    }

    // Link to ServiceRequest if available
    if (serviceRequestDetails) {
      if (serviceRequestDetails.id.startsWith('urn:uuid:')) {
        task.focus = { reference: serviceRequestDetails.id };
      } else {
        task.focus = { reference: `ServiceRequest/${serviceRequestDetails.id}` };
        //if Medication Request is in its display
        if (serviceRequestDetails.display && serviceRequestDetails.display.toLowerCase().includes('medication')) {
          task.focus = { reference: `MedicationRequest/${serviceRequestDetails.id}` };
        }
      }
    }

    // Add priority if specified
    if (taskConfig.taskPriority) {
      task.priority = taskConfig.taskPriority;
    }

    return task;
  }

  /**
   * STEP 3: Calculate execution period from date + time entry
   */
  private calculateExecutionPeriod(
    baseDate: Date,
    timeEntry: any,
    durationStr?: string
  ): { start: Date; end: Date } {
    const durationMinutes = this.parseDurationToMinutes(durationStr);

    // Any Time mode
    if (timeEntry.isAnyTimeInstead && timeEntry.timeValue === 'Any Time During The Day') {
      return {
        start: this.setTime(new Date(baseDate), 0, 0, 0),
        end: this.setTime(new Date(baseDate), 23, 59, 59)
      };
    }

    // Time Period mode (morning, afternoon, etc.)
    if (timeEntry.isPeriodInstead) {
      const period = timeEntry.timeValue?.toLowerCase();
      const range = this.PERIOD_TIME_RANGES[period as keyof typeof this.PERIOD_TIME_RANGES];
      if (range) {
        return {
          start: this.setTime(new Date(baseDate), range.start, 0, 0),
          end: this.setTime(new Date(baseDate), range.end, 0, 0)
        };
      }
    }

    // Time Range mode
    // Extract only the hours/minutes from the picker ISOs and apply them to baseDate,
    // so the correct recurrence date (Monday, Tuesday, etc.) is preserved.
    if (timeEntry.isTimeRangeInstead && timeEntry.timeValue?.includes('$#$')) {
      const [startISO, endISO] = timeEntry.timeValue.split('$#$');
      const pickerStart = new Date(startISO);
      const pickerEnd = new Date(endISO);
      return {
        start: this.setTime(new Date(baseDate), pickerStart.getHours(), pickerStart.getMinutes(), 0),
        end: this.setTime(new Date(baseDate), pickerEnd.getHours(), pickerEnd.getMinutes(), 0)
      };
    }

    // Specific Time mode (default)
    // Extract only hours/minutes from the picker value and merge onto baseDate,
    // preventing matTimepicker's default "today" date from overwriting the recurrence date.
    if (timeEntry.timeValue) {
      const pickerTime = new Date(timeEntry.timeValue);
      const start = this.setTime(new Date(baseDate), pickerTime.getHours(), pickerTime.getMinutes(), 0);
      const end = new Date(start.getTime() + durationMinutes * 60000);
      return { start, end };
    }

    // Fallback: use base date
    const start = new Date(baseDate);
    const end = new Date(start.getTime() + durationMinutes * 60000);
    return { start, end };
  }

  /**
   * Helper: Set time on a date
   */
  private setTime(date: Date, hours: number, minutes: number, seconds: number): Date {
    const d = new Date(date);
    d.setHours(hours, minutes, seconds, 0);
    return d;
  }

  /**
   * Helper: Parse duration string to minutes
   */
  private parseDurationToMinutes(durationStr?: string): number {
    if (!durationStr) return 30; // Default 30 minutes

    const str = durationStr.toLowerCase().trim();

    // Match patterns like "15 minutes", "1 hour", "30 mins", "2.5 hours"
    const hourMatch = str.match(/(\d+\.?\d*)\s*(hour|hr|h)/);
    const minMatch = str.match(/(\d+\.?\d*)\s*(minute|min|m)/);

    let minutes = 0;

    if (hourMatch) {
      minutes += parseFloat(hourMatch[1]) * 60;
    }
    if (minMatch) {
      minutes += parseFloat(minMatch[1]);
    }

    return minutes > 0 ? minutes : 30; // Fallback to 30 if parsing fails
  }

  /**
   * STEP 4: Generate unique group identifier for tasks from same form element
   */
  private generateGroupIdentifier(formIndex: number): any {
    const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');

    return {
      system: `${this.terminologyBaseUrl}/task-group`,
      value: `TG-${timestamp}-${formIndex}-${random}`
    };
  }

  /**
   * STEP 5: Check if task is duplicate (same focus + same execution period)
   * Compares both the date AND time to avoid false positives
   */
  private isDuplicateTask(
    newTask: any,
    existingTasks: any[],
    alreadyGenerated: any[]
  ): boolean {
    const newFocus = newTask.focus?.reference;
    const newStart = newTask?.restriction?.period?.start;
    const newEnd = newTask?.restriction?.period?.end;

    // If no focus reference, can't determine duplication reliably
    if (!newFocus || !newStart) {
      return false;
    }

    const checkList = [...existingTasks, ...alreadyGenerated];

    return checkList.some(existingTask => {
      const existingFocus = existingTask.focus?.reference;
      const existingStart = existingTask?.restriction?.period?.start;
      const existingEnd = existingTask?.restriction?.period?.end;

      // Must match both focus reference and execution start time
      const focusMatches = existingFocus === newFocus;
      const startMatches = new Date(existingStart).getTime() === new Date(newStart).getTime();

      // Optionally check end time as well for stricter duplicate detection
      const endMatches = existingEnd && newEnd
        ? new Date(existingEnd).getTime() === new Date(newEnd).getTime()
        : true; // If either end is missing, consider it a match based on start only

      const isDuplicate = focusMatches && startMatches && endMatches;

      if (isDuplicate) {
        console.log('Duplicate task detected:', {
          focus: newFocus,
          startTime: new Date(newStart).toISOString(),
          endTime: newEnd ? new Date(newEnd).toISOString() : 'N/A'
        });
      }

      return isDuplicate;
    });
  }

  /**
   * STEP 6: Fetch existing tasks for duplicate detection
   */
  private async fetchExistingTasks(patientId: string): Promise<any[]> {
    try {
      const url = `${this.backendUrl}/Task?for=Patient/${patientId}&status:not=completed,cancelled`;
      const bundle: any = await firstValueFrom(this.http.get(url));
      return bundle?.entry?.map((e: any) => e.resource) || [];
    } catch (error) {
      console.warn('Could not fetch existing tasks for duplicate check:', error);
      return [];
    }
  }

  /**
   * STEP 7: Submit tasks via Bundle transaction
   */
  private async submitTasksBundle(tasks: any[], extraEntries: any[] = []): Promise<void> {
    const bundleEntries = tasks.map(task => ({
      request: {
        method: 'POST',
        url: 'Task'
      },
      resource: task
    }));

    const bundle: any = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [...bundleEntries, ...extraEntries]
    };

    console.log('📦 Submitting task bundle:', bundle);
    console.log(`📊 Total tasks: ${tasks.length}`);

    // Open preview dialog before submission
    this.openTaskPreviewDialog(bundle, tasks);
    //     // Update StateService with new tasks
    //     this.stateService.processBundleTransaction(result);

    //     this.snackBar.openFromComponent(SuccessMessageComponent, {
    //       data: { message: `Successfully created ${tasks.length} task${tasks.length !== 1 ? 's' : ''}` },
    //       duration: 3000,
    //       horizontalPosition: 'end',
    //       verticalPosition: 'top',
    //       panelClass: ['success-snackbar']
    //     });

    //     // Reset form and close dialog
    //     this.overallTasksForm?.reset();
    //     if (this.dref) {
    //       this.dref.close(result);
    //     }
    //   },
    //   error: (err) => {
    //     console.error('❌ Task bundle submission failed:', err);
    //     this.errorService.openandCloseError(
    //       `Failed to submit tasks: ${err.error?.issue?.[0]?.diagnostics || err.message || 'Unknown error'}`
    //     );
    //   }
    // });
  }

  /**
   * Open task preview dialog to review generated tasks before submission
   * @param bundle - FHIR Bundle containing all task entries
   * @param allTasks - Array of all generated Task resources
   */
  private openTaskPreviewDialog(bundle: any, allTasks: any[]): void {
    const patientId = this.stateService.currentPatientId$.getValue();

    // Group tasks by groupIdentifier for better organization
    const taskGroups = new Map<string, any[]>();
    allTasks.forEach(task => {
      const groupId = task.groupIdentifier?.value || 'ungrouped';
      if (!taskGroups.has(groupId)) {
        taskGroups.set(groupId, []);
      }
      taskGroups.get(groupId)!.push(task);
    });

    // Prepare dialog data
    const dialogData = {
      bundle: bundle,
      allTasks: allTasks,
      taskGroups: taskGroups,
      patientId: patientId || 'Unknown',
      totalCount: allTasks.length,
      groupCount: taskGroups.size
    };

    console.log('📋 Opening task preview dialog with data:', dialogData);

    // TODO: Open your task-preview dialog component here
    // Example:
    const dialogRef = this.dialog.open(TaskPreviewDialogComponent, {
      width: '90vw',
      maxWidth: '900px',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        const confirmedTasks = result.tasks || allTasks;
        this.submitBundleToBackend(this.withConfirmedTasks(bundle, confirmedTasks), confirmedTasks);
      }
    });
  }

  private withConfirmedTasks(bundle: any, confirmedTasks: any[]): any {
    const nextBundle = JSON.parse(JSON.stringify(bundle));
    const cleanConfirmedTasks = confirmedTasks.map(task => this.sanitizeTaskForPreviewSubmission(task));
    const taskEntries = (nextBundle.entry || []).filter((entry: any) => entry.resource?.resourceType === 'Task');
    let nextTaskIndex = 0;

    nextBundle.entry = (nextBundle.entry || []).filter((entry: any) => {
      if (entry.resource?.resourceType !== 'Task') {
        return true;
      }

      const task = cleanConfirmedTasks[nextTaskIndex++];
      if (!task) {
        return false;
      }

      entry.resource = task;
      return true;
    });

    if (cleanConfirmedTasks.length > taskEntries.length) {
      cleanConfirmedTasks.slice(taskEntries.length).forEach((task: any) => {
        nextBundle.entry.push({
          resource: task,
          request: {
            method: 'POST',
            url: 'Task'
          }
        });
      });
    }

    // 1. Identify which cloned ServiceRequest / MedicationRequest UUIDs are still used by confirmed tasks
    const activeSrUuids = new Set<string>();
    cleanConfirmedTasks.forEach((t: any) => {
      const ref = t.focus?.reference;
      if (typeof ref === 'string' && ref.startsWith('urn:uuid:')) {
        activeSrUuids.add(ref);
      }
    });

    // 2. Identify orphaned cloned request UUIDs (cloned SRs whose tasks were removed)
    const orphanedSrUuids = new Set<string>();
    (nextBundle.entry || []).forEach((e: any) => {
      const resType = e.resource?.resourceType;
      if ((resType === 'ServiceRequest' || resType === 'MedicationRequest') &&
          typeof e.fullUrl === 'string' && e.fullUrl.startsWith('urn:uuid:') && !activeSrUuids.has(e.fullUrl)) {
        orphanedSrUuids.add(e.fullUrl);
      }
    });

    if (orphanedSrUuids.size > 0) {
      // 3. Find placeholder service entries (Observation, Procedure, ImagingStudy) based on orphaned SRs
      const orphanedPlaceholderUuids = new Set<string>();
      (nextBundle.entry || []).forEach((e: any) => {
        const basedOnRefs = (e.resource?.basedOn || []).map((b: any) => b?.reference);
        if (basedOnRefs.some((ref: string) => orphanedSrUuids.has(ref))) {
          if (typeof e.fullUrl === 'string') orphanedPlaceholderUuids.add(e.fullUrl);
        }
      });

      // 4. Find ChargeItems referencing orphaned SRs or orphaned placeholders
      const orphanedChargeItemUuids = new Set<string>();
      (nextBundle.entry || []).forEach((e: any) => {
        if (e.resource?.resourceType === 'ChargeItem') {
          const serviceRefs = (e.resource?.service || []).map((s: any) => s?.reference);
          const matchesOrphan = serviceRefs.some((ref: string) =>
            orphanedSrUuids.has(ref) || orphanedPlaceholderUuids.has(ref)
          );
          if (matchesOrphan && typeof e.fullUrl === 'string') {
            orphanedChargeItemUuids.add(e.fullUrl);
          }
        }
      });

      // 5. Filter out all orphaned resources (SRs, placeholders, ChargeItems, and Invoices)
      nextBundle.entry = nextBundle.entry.filter((e: any) => {
        if (orphanedSrUuids.has(e.fullUrl)) return false;
        if (orphanedPlaceholderUuids.has(e.fullUrl)) return false;
        if (orphanedChargeItemUuids.has(e.fullUrl)) return false;

        if (e.resource?.resourceType === 'Invoice' && Array.isArray(e.resource?.lineItem)) {
          const hasOrphanCharge = e.resource.lineItem.some((li: any) =>
            orphanedChargeItemUuids.has(li.chargeItemReference?.reference)
          );
          if (hasOrphanCharge) return false;
        }

        return true;
      });
    }

    return nextBundle;
  }

  private sanitizeTaskForPreviewSubmission(task: any): any {
    const cleanTask = JSON.parse(JSON.stringify(task));
    [
      'patientDetails',
      'startDate',
      'dueDate',
      'taskName',
      'assignedTo',
      'overdue',
      'focusCategory'
    ].forEach(key => delete cleanTask[key]);

    Object.keys(cleanTask).forEach(key => {
      if (key.startsWith('_')) {
        delete cleanTask[key];
      }
    });

    return cleanTask;
  }

  /**
   * Actually submit the bundle to the backend after preview confirmation
   * @param bundle - FHIR Bundle to submit
   * @param tasks - Array of tasks being submitted
   */
  private submitBundleToBackend(bundle: any, tasks: any[]): void {
    this.http.post(`${this.backendUrl}`, bundle).subscribe({
      next: (result: any) => {
        console.log('✅ Task bundle submitted successfully:', result);

        // Update StateService with new tasks
        this.stateService.processBundleTransaction(result);

        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: `Successfully created ${tasks.length} task${tasks.length !== 1 ? 's' : ''}` },
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });

        // Reset form and close dialog
        this.overallTasksForm?.reset();
        if (this.dref) {
          this.dref.close(result);
        }
      },
      error: (err) => {
        console.error('❌ Task bundle submission failed:', err);
        this.errorService.openandCloseError(
          `Failed to submit tasks: ${err.error?.issue?.[0]?.diagnostics || err.message || 'Unknown error'}`
        );
      }
    });
  }
  snackBar = inject(MatSnackBar);
  // ...existing code...


  /* Fetch active medication requests for the current patient
  */
  private async fetchActiveMedicationRequests(): Promise<any[]> {
    const patientId = await firstValueFrom(this.stateService.currentPatientId$);

    if (!patientId) {
      console.error('No patient ID available');
      return [];
    }

    try {
      const url = `${this.backendUrl}/MedicationRequest?patient=${patientId}&status=active&_sort=-_lastUpdated`;
      const bundle: any = await firstValueFrom(this.http.get(url));

      const medicationRequests = bundle?.entry?.map((entry: any) => entry.resource) || [];
      console.log('Fetched active medication requests:', medicationRequests);

      return medicationRequests;
    } catch (error) {
      console.error('Error fetching medication requests:', error);
      return [];
    }

  }

  /**
 * Opens dialog to select from existing active medication requests
 * @param taskIndex - The index of the task
 */
  async openSelectExistingMedicationRequest(taskIndex: number) {
    const existingMedRequests = await this.fetchActiveMedicationRequests();

    if (existingMedRequests.length === 0) {
      this.errorService.openandCloseError('No active medication orders found for this patient.');
      return;
    }

    // Transform medication requests into reference field format
    const medicationReferences = existingMedRequests.map(mr => ({
      reference: `MedicationRequest/${mr.id}`,
      display: mr.medicationCodeableConcept?.text ||
        mr.medicationCodeableConcept?.coding?.[0]?.display ||
        `Medication Request ${mr.id}`,
      resource: mr
    }));

    const dialogRef = this.dialog.open(DynamicFormsV2Component, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        formFields: [
          <IndividualReferenceField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'medicationRequest',
              fieldName: "Select Medication Order",
              fieldLabel: "Choose from existing medication orders",
              fieldType: 'IndividualReferenceField',
              inputType: 'select',
              isArray: false,
              isGroup: false,
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
            },
            data: medicationReferences
          }
        ],
        formMetaData: {
          formName: 'Select Existing Medication',
          formDescription: 'Choose from active medication orders for this patient',
          closeDialogOnSubmit: true
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.values?.medicationRequest) {
        const selectedRef = result.values.medicationRequest;

        const selectedMR = existingMedRequests.find(mr =>
          `MedicationRequest/${mr.id}` === selectedRef || selectedRef.reference === `MedicationRequest/${mr.id}`
        );

        if (selectedMR) {
          const task = this.taskFormArray.at(taskIndex);
          task.get(['taskFocus'])?.setValue(selectedRef);

          const mrId = selectedMR.id || '';
          const mrCode = selectedMR.medicationCodeableConcept?.coding?.[0]?.code ||
            selectedMR.medicationCodeableConcept?.text || 'N/A';
          const mrDisplay = selectedMR.medicationCodeableConcept?.text ||
            selectedMR.medicationCodeableConcept?.coding?.[0]?.display ||
            `Medication Request ${mrId}`;

          this.taskServiceRequests.set(taskIndex, {
            id: mrId,
            code: mrCode,
            display: mrDisplay,
            resource: selectedMR
          });

          console.log('Selected medication request for task', taskIndex, this.taskServiceRequests.get(taskIndex));
        }
      }
    });
  }

  /**
   * Opens the medication request dialog to add a new medication order
   * @param taskIndex - The index of the task to add the medication request to
   */
  async openAddNewMedicationRequest(taskIndex: number) {
    const patientId = await firstValueFrom(this.stateService.currentPatientId$);

    const existingList = this.stateService.PatientResources.medicationRequests.getValue() || [];
    const existingIds = new Set(
      existingList
        .map(mr => mr?.actualResource?.id || mr?.referenceId)
        .filter(Boolean)
    );

    this.ecServ.addMedicationRequest(patientId || "");

    const subscription = this.stateService.PatientResources.medicationRequests.subscribe(medRequests => {
      const newEntry = (medRequests || []).find(mr => {
        const id = mr?.actualResource?.id || mr?.referenceId;
        return id && !existingIds.has(id);
      });

      const newMR = newEntry?.actualResource || (medRequests.length > existingIds.size ? medRequests[0]?.actualResource : null);

      if (newMR?.id) {
        const task = this.taskFormArray.at(taskIndex);
        task.get(['taskFocus'])?.setValue(`MedicationRequest/${newMR.id}`);

        const mrId = newMR.id;
        const mrCode = (newMR as any).medicationCodeableConcept?.coding?.[0]?.code ||
          (newMR as any).medicationCodeableConcept?.text || 'N/A';
        const mrDisplay = (newMR as any).medicationCodeableConcept?.text ||
          (newMR as any).medicationCodeableConcept?.coding?.[0]?.display ||
          `Medication Request ${mrId}`;

        this.taskServiceRequests.set(taskIndex, {
          id: mrId,
          code: mrCode,
          display: mrDisplay,
          resource: newMR
        });

        console.log('Created new medication request for task', taskIndex, this.taskServiceRequests.get(taskIndex));

        subscription.unsubscribe();
      }
    });

    const dialogSub = this.dialog.afterAllClosed.subscribe(() => {
      setTimeout(() => {
        subscription.unsubscribe();
        dialogSub.unsubscribe();
      }, 3000);
    });
  }


}
